import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  combineLatestWith,
  debounceTime,
  first,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  tap,
  throwError
} from 'rxjs';

import { AddressChoice } from '../api/v1/models/address-choice';
import { AddressDto } from '../api/v1/models/address-dto';
import { City } from '../api/v1/models/city';
import { Coordinates } from '../api/v1/models/coordinates';
import { DeliveryZoneResponse } from '../api/v1/models/delivery-zone-response';
import { AddressService } from '../api/v1/services/address.service';
import { ConfigRepository } from '../shared/repositories/config.repository';
import { SessionRepository } from '../shared/repositories/session.repository';
import { concatMap, filter } from 'rxjs/operators';
import { AddressHint } from '../api/v1/models/address-hint';
import { CostRule } from '../api/v1/models/cost-rule';
import { DayNamesList } from '../shared/data/day-names';
import { WorkingHours } from '../api/v1/models/working-hours';
import { CoordinatesDto } from '../api/v1/models/coordinates-dto';
import { DeliveryType } from '../api/v1/models';
import { StatusMessageService } from '../shared/services/status-message.service';

interface DeliveryZoneParams {
  address?: AddressDto | null,
  coordinates?: Coordinates | null,
}

@Injectable({
  providedIn: 'root'
})
export class CitiesService {
  constructor(
    private configRepository: ConfigRepository,
    private addressService: AddressService,
    private sessionRepository: SessionRepository,
    private statusMessage: StatusMessageService,
  ) {
    this.subscribeToDeliveryZoneByAddress();
    this.subscribeToDeliveryZoneByCoordinates();
  }

  private _cities: BehaviorSubject<City[] | null> = new BehaviorSubject<City[] | null>(null);

  public changePostcodeSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  private _postcodeAddress: BehaviorSubject<AddressChoice[] | null> = new BehaviorSubject<AddressChoice[] | null>(null)
  public postcodeAddress$: Observable<AddressChoice[] | null> =
    new BehaviorSubject<AddressChoice[] | null>(null).asObservable().pipe(
      switchMap(() => {
        return this.changePostcodeSubject.pipe(
          concatMap(postcode => {
            if(!postcode) {return of(null)}
            if(this.currentPostCode == postcode) {
              return this._postcodeAddress.asObservable()
            }

            this.currentPostCode = postcode;
            return this.getAddressByPostCode(postcode);
          }),
        )
      })
    )

  private _isLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isLoading$: Observable<boolean> = this._isLoading.asObservable();

  private _isLoadingDeliveryZone: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isLoadingDeliveryZone$: Observable<boolean> = this._isLoadingDeliveryZone.asObservable();

  private _isLoadedDeliveryZone: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isLoadedDeliveryZone$: Observable<boolean> = this._isLoadedDeliveryZone.asObservable();

  private _isLoadingHints: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isLoadingHints$: Observable<boolean> = this._isLoadingHints.asObservable();

  private currentPostCode: string;

  checkDeliveryZones(){
    this.sessionRepository.deliveryAddress$.pipe(
      first(),
      combineLatestWith(
        this.sessionRepository.isDelivery$.pipe(first()),
        this.configRepository.addressFieldSettings$
      ),
      filter(([deliveryAddress, isDelivery, addressFieldSettings]) =>
        isDelivery && !!deliveryAddress && !addressFieldSettings.usesIndexSearch
      ),
      switchMap(([deliveryAddress]) => {
        return this.getDeliveryZoneByAddress(deliveryAddress!);
      })
    ).subscribe();

    this.sessionRepository.deliveryCoordinates$.pipe(
      first(),
      combineLatestWith(
        this.sessionRepository.isDelivery$.pipe(first()),
        this.configRepository.addressFieldSettings$
      ),
      filter(([deliveryCoordinates, isDelivery, addressFieldSettings]) =>
        isDelivery && !!deliveryCoordinates && addressFieldSettings.usesIndexSearch
      ),
      switchMap(([deliveryCoordinates]) => {
        return this.getDeliveryZoneByCoordinates(deliveryCoordinates!);
      })
    ).subscribe()
  }

  getNearestInterval(workingHours: {[key: string]: WorkingHours[]}): CostRule[] | null {
    const workingDays: Set<string> = new Set(Object.keys(workingHours));
    const todayIndex: number = new Date().getDay();

    const normalizedIndex = (todayIndex + 6) % 7;

    for (let i = 0; i < 7; i++) {
      const nextDayIndex: number = (normalizedIndex + i) % 7;
      const nextDay: string = DayNamesList[nextDayIndex];
      if (workingDays.has(nextDay)) {
        return workingHours[nextDay][0].costRules;
      }
    }

    return null;
  }

  get cities$(): Observable<City[] | null> {
    if(this._cities.value) {
      return this._cities.asObservable();
    }

    this._isLoading.next(true);
    return of(null).pipe(
      switchMap(() => {
        if(this._cities.value) {
          return this._cities.asObservable();
        }
        return this.getAddressJson();
      }),
      catchError(error => {
        this._isLoading.next(false);
        return throwError(error);
      })
    )
  }

  public loadAddressByPostcode(postcode: string): void {
    this.changePostcodeSubject.next(postcode);
  }

  public getDeliveryZoneByAddress(address: AddressDto): Observable<DeliveryZoneResponse> {
    return this.getDeliveryZoneForOrder({ address: address });
  }

  public getDeliveryZoneByCoordinates(coordinates: Coordinates): Observable<DeliveryZoneResponse> {
    return this.getDeliveryZoneForOrder({ coordinates: coordinates });
  }

  private fetchDeliveryZones(deliveryZoneParams: DeliveryZoneParams): Observable<DeliveryZoneResponse> {
    const storeId = this.configRepository.activeOutletId;
    return this.sessionRepository.selectedDeliveryType$.pipe(
      first(),
      filter((selectedDeliveryType): selectedDeliveryType is DeliveryType => !!selectedDeliveryType),
      switchMap(selectedDeliveryType => this.addressService.v1AddressGetDeliveryZonePost$Json({
        body: {
          ...deliveryZoneParams,
          storeId: storeId,
          deliveryType: selectedDeliveryType,
        }
      })),
      map(response => {
        if (response.error || !response.result) {
          throw response;
        }
        return response.result;
      }),
    )
  }

  private getDeliveryZoneForOrder(deliveryZoneParams: DeliveryZoneParams): Observable<DeliveryZoneResponse> {
    this._isLoadingDeliveryZone.next(true)

    return this.fetchDeliveryZones(deliveryZoneParams).pipe(
      tap((response) => {
        this._isLoadingDeliveryZone.next(false);

        this.configRepository.setActiveDeliveryZone(response);

        const initialCostRules = this.getNearestInterval(response.workIntervals);
        this.configRepository.setActiveCostRules(initialCostRules);

        this._isLoadedDeliveryZone.next(true);

        if(response.storeId) {
          this.configRepository.setActiveOutlet(response.storeId)
        }

        console.info(`Delivery: lat ${response?.coordinates?.lat}, lng ${response?.coordinates?.lng}`);

        if(!response.deliveryZoneId) {
          throw response;
        }
      }),
      catchError(error => {
        this._isLoadingDeliveryZone.next(false);

        this.configRepository.setActiveDeliveryZone(null);
        this.configRepository.setActiveCostRules(null);

        this.statusMessage.setStatusMessage({
          title: $localize `:@@MessageModalTitleError:Error`,
          message: $localize `:@@AddressNotServed:Oops! Unfortunately, we do not serve the specified address yet.`,
          type: 'error'
        })

        this.sessionRepository.setDeliveryAddress(null);

        throw error;
      })
    )
  }

  private getAddressJson(): Observable<City[]> {
    const storeId = this.configRepository.activeOutletId;
    return this.addressService.v1AddressStreetsStoreIdGet$Json({ storeId }).pipe(
      map(response => {
        if(response.error || !response.result) {
          throw response;
        }
        return response.result;
      }),
      tap(data => {
        this._isLoading.next(false);
        this._cities.next(data);
      }),
      catchError(error => {
        this._isLoading.next(false);
        return throwError(error);
      })
    );
  }

  private getAddressByPostCode(postcode: string): Observable<AddressChoice[]> {
    this._isLoading.next(true);
    return this.addressService.v1AddressAddressesByPostcodeGet$Json({ postCode: postcode }).pipe(
      map(response => {
        if(response.error || !response.result) {
          throw response;
        }
        return response.result;
      }),
      tap(data => {
        this._isLoading.next(false);
        this._postcodeAddress.next(data);
      }),
      catchError(error => {
        this._isLoading.next(false);
        return throwError(error);
      })
    )
  }

  fetchHints(query: string, coordinates: CoordinatesDto | null): Observable<AddressHint[]> {
    this._isLoadingHints.next(true);

    return this.addressService.v1AddressHintsPost$Json({
      body: {
        query: query,
        storeId: this.configRepository.activeOutletId,
        coordinates: coordinates || this.configRepository.activeOutlet?.coordinates || null
      }
    }).pipe(
      map(response => {
        if (response.error || !response.result) {
          throw response;
        }
        return response.result;
      }),
      map(response => response.hints),
      tap(() => this._isLoadingHints.next(false)),
    )
  }

  private hintSearchString$: Subject<string> = new Subject<string>();

  setHintSearchString(value: string): void {
    this.hintSearchString$.next(value);
  }

  hints$: Observable<AddressHint[]> = this.hintSearchString$.pipe(
    debounceTime(650),
    filter((value): value is string => !!value && value.length >= 4),
    combineLatestWith(
      this.configRepository.userPosition$.pipe(first())
    ),
    switchMap(([value, userPosition]) => this.fetchHints(value, userPosition)),
  )

  private _deliveryZoneByAddressSubj$: Subject<AddressDto> = new Subject<AddressDto>();
  setDeliveryAddress(address: AddressDto){
    this._deliveryZoneByAddressSubj$.next(address);
  }

  private _deliveryZoneByCoordinatesSubj$: Subject<Coordinates> = new Subject<Coordinates>();
  setDeliveryCoordinates(coordinates: Coordinates){
    this._deliveryZoneByCoordinatesSubj$.next(coordinates);
  }

  private subscribeToDeliveryZoneByAddress(){
    this._deliveryZoneByAddressSubj$.asObservable().pipe(
      switchMap(address => this.getDeliveryZoneByAddress(address)),
    ).subscribe({
      error: err => {
        this.subscribeToDeliveryZoneByAddress();
      }
    });
  }
  private subscribeToDeliveryZoneByCoordinates(){
    this._deliveryZoneByCoordinatesSubj$.asObservable().pipe(
      switchMap(coordinates => this.getDeliveryZoneByCoordinates(coordinates)),
    ).subscribe({
      error: err => {
        this.subscribeToDeliveryZoneByCoordinates();
      }
    });
  }

}
